import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, Download, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import VideoPlayer from "@/components/VideoPlayer";
import { AccessBadge, DemoTag, VideoCard } from "@/components/ui-lux";
import { NotFound } from "@/pages/Misc";
import { LoadError } from "@/lib/useApi";

export const BookmarkButton = ({ type, id, initial }) => {
  const { user } = useAuth();
  const [on, setOn] = useState(initial);
  useEffect(() => setOn(initial), [initial]);
  if (!user) return null;
  const toggle = async () => {
    try {
      const { data } = await api.post("/me/bookmarks/toggle", { item_type: type, item_id: id });
      setOn(data.bookmarked);
      toast.success(data.bookmarked ? "Saved to your bookmarks" : "Removed from bookmarks");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button onClick={toggle} className="btn-outline !py-2.5 !px-5" data-testid="bookmark-toggle-btn">
      {on ? <BookmarkCheck size={16} className="text-[#D4AF37]" /> : <Bookmark size={16} />}{on ? "Saved" : "Save"}
    </button>
  );
};

export const LockedPanel = ({ access }) => {
  const { user } = useAuth();
  return (
    <div className="aspect-video rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1C55] to-black flex flex-col items-center justify-center text-center p-8" data-testid="locked-panel">
      <Lock className="text-[#D4AF37]" size={32} />
      <h3 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-white mt-4">This teaching is free for members</h3>
      <p className="text-slate-300 mt-3 max-w-md text-sm">Create a free account or log in to continue.</p>
      <div className="flex gap-3 mt-6">
        {!user && <Link to="/register" className="btn-gold" data-testid="locked-register-btn">Join free</Link>}
        <Link to={user ? "/courses" : "/login"} className="btn-outline" data-testid="locked-cta-btn">{user ? "View courses" : "Log in"}</Link>
      </div>
    </div>
  );
};

export default function VideoDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [prog, setProg] = useState({ progress: null, bookmarked: false });
  useSEO(v && v !== "error" ? { title: v.seo_title || v.title, description: v.meta_description || v.description?.slice(0, 155), image: v.thumbnail,
    jsonLd: { "@context": "https://schema.org", "@type": "VideoObject", name: v.title, description: v.description, thumbnailUrl: v.thumbnail, uploadDate: v.created_at } } : {});
  const load = () => {
    setV(undefined);
    api.get(`/content/videos/${slug}`).then((r) => {
      setV(r.data);
      api.get("/content/videos").then((x) => {
        const real = x.data.filter((i) => i.id !== r.data.id && !i.is_demo);
        setRelated([...real.filter((i) => i.category === r.data.category), ...real.filter((i) => i.category !== r.data.category)].slice(0, 4));
      }).catch(() => {});
      if (user) api.get(`/me/progress/${r.data.id}`).then((x) => setProg(x.data)).catch(() => {});
    }).catch((e) => setV(e.response?.status === 404 ? null : "error"));
  };
  useEffect(load, [slug, user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (v === null) return <NotFound />;
  if (v === "error") return <div className="pt-40 container-x"><LoadError onRetry={load} /></div>;
  if (!v) return <div className="min-h-screen" />;
  const save = (position, duration, completed) => user && api.post("/me/progress", { item_type: "video", item_id: v.id, position, duration, completed }).catch(() => {});
  return (
    <div className="pt-36 container-x" data-testid="video-detail-page">
      <div className="grid lg:grid-cols-[1fr_340px] gap-10">
        <div>
          {v.locked ? <LockedPanel access={v.access} /> : (
            <VideoPlayer url={v.video_url} poster={v.thumbnail} title={v.title} startAt={prog.progress?.position || 0}
              onProgress={(t, d) => save(t, d)} onEnded={(d) => save(d, d, true)} />
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3"><AccessBadge access={v.access} locked={v.locked} /><DemoTag show={v.is_demo} /><span className="text-sm text-slate-400">{v.category}</span></div>
          <h1 className="font-serif text-3xl sm:text-5xl font-semibold tracking-[-0.04em] text-white mt-4" data-testid="video-title">{v.title}</h1>
          <div className="mt-6 flex gap-3"><BookmarkButton type="video" id={v.id} initial={prog.bookmarked} /></div>
          <p className="mt-8 text-slate-300 leading-relaxed whitespace-pre-line max-w-3xl">{v.description}</p>
          {!v.locked && v.attachments?.length > 0 && (
            <div className="mt-10" data-testid="video-attachments">
              <p className="eyebrow">Resources</p>
              <div className="flex flex-wrap gap-3">
                {v.attachments.map((a) => <a key={a.url} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="btn-outline !py-2.5"><Download size={15} />{a.title}</a>)}
              </div>
            </div>
          )}
        </div>
        <aside>
          <p className="eyebrow">Related teachings</p>
          <div className="space-y-6">{related.map((r) => <VideoCard key={r.id} v={r} wide />)}</div>
        </aside>
      </div>
    </div>
  );
}
